// Слои раздела «Снимки»: отметки и РАМКИ ОСМОТРА.
//
// Слоёв обязательно два, и второй не украшение. Отметки без рамок отвечают на
// один вопрос («что нашли») и молчат про второй, более важный: где мы вообще
// смотрели. Пустая карта без рамок значит «сюда не заглядывали», пустая карта
// с рамками — «искали, не нашли», и картинка у них одинаковая.
//
// Отрисовка и подключение к карте живут в `ImageryPage.tsx`; здесь только то,
// что проверяется без карты.

import type maplibregl from 'maplibre-gl'

import { aisStroke, verdictColor, type AisState } from './verdictStyle'

export const MARKS_SOURCE = 'imagery-marks'
export const LOOKED_SOURCE = 'imagery-looked'
export const MARKS_LAYER = 'imagery-marks-dots'
export const LOOKED_FILL_LAYER = 'imagery-looked-fill'
export const LOOKED_LINE_LAYER = 'imagery-looked-line'
//: Луч следа. Слой ОТДЕЛЬНЫЙ от отметок и лежит ПОД ними: точка — это ответ
//: («здесь судно»), а луч — улика, на которой ответ держится, и перекрывать
//: собой ответ улика не должна.
export const WAKE_SOURCE = 'imagery-wakes'
export const WAKE_LAYER = 'imagery-wake-rays'

export const VERDICTS = [
  'mark', 'vessel', 'cloud', 'conflict', 'unconfirmed', 'obscured',
] as const

const AIS_STATES: AisState[] = ['matched', 'unmatched', 'not_asked']

/** Цвет точки по вердикту — expr data-driven, а не перебор слоёв.
 *
 *  Незнакомый вердикт получает свой цвет из `verdictColor`, а не пропадает:
 *  исчезнувшая точка неотличима от «прибор её не нашёл». */
export function verdictColorExpression(): maplibregl.ExpressionSpecification {
  const expr: unknown[] = ['match', ['get', 'verdict']]
  for (const v of VERDICTS) expr.push(v, verdictColor(v))
  expr.push(verdictColor('__other__'))
  return expr as maplibregl.ExpressionSpecification
}

/** Обводка по состоянию сведения. Состояние приезжает свойством `ais_state`:
 *  считать его выражением MapLibre из вложенного объекта нельзя. */
export function aisStrokeColorExpression(): maplibregl.ExpressionSpecification {
  const expr: unknown[] = ['match', ['get', 'ais_state']]
  for (const s of AIS_STATES) expr.push(s, aisStroke(s).color)
  expr.push(aisStroke('not_asked').color)
  return expr as maplibregl.ExpressionSpecification
}

export function aisStrokeWidthExpression(): maplibregl.ExpressionSpecification {
  const expr: unknown[] = ['match', ['get', 'ais_state']]
  for (const s of AIS_STATES) expr.push(s, aisStroke(s).width)
  expr.push(aisStroke('not_asked').width)
  return expr as maplibregl.ExpressionSpecification
}

/** Отбор фичей по вердикту и прибору. Пустой asList значит «все», а не «ни одного».
 *
 *  Пустой отбор, прочитанный как «ничего не показывать», гасит карту в тот
 *  момент, когда человек снял последнюю галочку, — и выглядит это как поломка.
 *  То же правило, что у `species_region`: ПУСТО = ВЕЗДЕ. */
export function markFilter(verdicts: string[], sensors: string[]): maplibregl.FilterSpecification | null {
  const clauses: unknown[] = []
  if (verdicts.length > 0) clauses.push(['in', ['get', 'verdict'], ['literal', verdicts]])
  if (sensors.length > 0) clauses.push(['in', ['get', 'sensor'], ['literal', sensors]])
  if (clauses.length === 0) return null
  return (clauses.length === 1 ? clauses[0] : ['all', ...clauses]) as maplibregl.FilterSpecification
}

/** Свойства фичи для карты: вложенный `ais` разворачивается в плоское поле.
 *
 *  MapLibre читает только плоские свойства, и `['get', 'ais']` вернул бы объект,
 *  который `match` сравнить не с чем — слой молча покрасился бы умолчанием. */
export function flattenMark(props: Record<string, unknown>): Record<string, unknown> {
  const ais = props.ais as { matched?: boolean } | null | undefined
  return {
    ...props,
    ais_state: ais == null ? 'not_asked' : ais.matched ? 'matched' : 'unmatched',
  }
}

/** Сводка вердиктов по ПОКАЗАННЫМ отметкам.
 *
 *  Сервер присылает свою сводку по ВСЕМУ кадру, включая осмотры, отметки
 *  которых сейчас не рисуются (выбрана другая подложка). Показанная рядом с
 *  картой, такая сводка утверждает отметку, которой на экране нет, — а человек
 *  читает расхождение как пропажу точек, а не как отбор по снимку. */
export function verdictFacets(
  features: { properties: { verdict?: unknown } }[],
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const f of features) {
    const v = f.properties?.verdict
    if (typeof v !== 'string' || v === '') continue
    out[v] = (out[v] ?? 0) + 1
  }
  return out
}

/** Строки легенды: вердикты, встреченные в кадре. Пустых строк не рисуем —
 *  легенда, обещающая вердикт, которого в кадре нет, читается как пропажа. */
export function legendRows(facets: Record<string, number> | undefined) {
  return VERDICTS
    .filter(v => (facets?.[v] ?? 0) > 0)
    .map(v => ({ verdict: v, color: verdictColor(v), count: facets?.[v] ?? 0 }))
}
